import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import {Alert, Button, Form, Spinner} from 'react-bootstrap';
import {FaBell} from 'react-icons/fa';
import {useTranslation} from 'react-i18next';
import {getNotificationPreferences, saveNotificationPreferences} from '../api';
import {isWebPushSupported, subscribeBrowserToWebPush} from '../util/web-push';

const preferenceItems = [
  ['openMatches', 'WEB_PUSH_OPEN_MATCHES', 'WEB_PUSH_OPEN_MATCHES_HELP'],
  ['openMatchInvites', 'WEB_PUSH_INVITES', 'WEB_PUSH_INVITES_HELP'],
  ['openMatchChat', 'WEB_PUSH_CHAT', 'WEB_PUSH_CHAT_HELP'],
  ['openMatchResults', 'WEB_PUSH_RESULTS', 'WEB_PUSH_RESULTS_HELP']
];

const defaultPreferences = {
  openMatches: true,
  openMatchInvites: true,
  openMatchChat: true,
  openMatchResults: true
};

const WebPushSettings = ({token, compact}) => {
  const {t} = useTranslation();
  const [preferences, setPreferences] = useState(defaultPreferences);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [message, setMessage] = useState(null);
  const supported = typeof window !== 'undefined' && isWebPushSupported();
  const permission = supported ? Notification.permission : 'unsupported';

  useEffect(() => {
    if (!token) return;
    getNotificationPreferences(token).then(response => {
      if (response.status === 200 && response.json.preferences) {
        setPreferences(response.json.preferences);
      }
    }).finally(() => setLoading(false));
  }, [token]);

  const updatePreference = key => async event => {
    const next = {...preferences, [key]: event.target.checked};
    setPreferences(next);
    setSaving(true);
    const response = await saveNotificationPreferences(next, token);
    setSaving(false);
    if (response.status !== 200) setMessage({type: 'danger', text: t('WEB_PUSH_SAVE_ERROR')});
  };

  const subscribe = async () => {
    setSubscribing(true);
    setMessage(null);
    const result = await subscribeBrowserToWebPush(token);
    setSubscribing(false);
    if (result.ok) {
      setMessage({type: 'success', text: t('WEB_PUSH_ENABLED')});
    } else if (result.reason === 'denied') {
      setMessage({type: 'warning', text: t('WEB_PUSH_DENIED')});
    } else if (result.reason === 'not_configured') {
      setMessage({type: 'warning', text: t('WEB_PUSH_NOT_CONFIGURED')});
    } else {
      setMessage({type: 'warning', text: t('WEB_PUSH_UNSUPPORTED')});
    }
  };

  if (!token) return null;
  return <section className={compact ? 'web-push-settings is-compact' : 'web-push-settings'}>
    <div className="web-push-settings__head">
      <span><FaBell/> {t('WEB_PUSH_TITLE')}</span>
      {supported && permission === 'granted' ? <small>{t('WEB_PUSH_ACTIVE')}</small> : null}
    </div>
    {message ? <Alert variant={message.type}>{message.text}</Alert> : null}
    {!supported ? <p className="web-push-settings__hint">{t('WEB_PUSH_IPHONE_HINT')}</p> : null}
    {loading ? <Spinner animation="border" size="sm"/> : <div className="web-push-settings__grid">
      {preferenceItems.map(([key, labelKey, helpKey]) => <Form.Check
        key={key}
        id={`web-push-${key}`}
        type="switch"
        label={<span><strong>{t(labelKey)}</strong><small>{t(helpKey)}</small></span>}
        checked={Boolean(preferences[key])}
        onChange={updatePreference(key)}
      />)}
    </div>}
    <Button type="button" onClick={subscribe} disabled={!supported || subscribing || saving}>
      {t(subscribing ? 'WEB_PUSH_ENABLING' : permission === 'granted' ? 'WEB_PUSH_UPDATE_DEVICE' : 'WEB_PUSH_ENABLE')}
    </Button>
  </section>;
};

WebPushSettings.propTypes = {token: PropTypes.string, compact: PropTypes.bool};
WebPushSettings.defaultProps = {token: null, compact: false};

export default WebPushSettings;
